import { PropsWithChildren, ReactNode } from 'react';

import {
  CircularProgress,
  type ColorPaletteProp,
  Sheet,
  Stack,
  Typography,
} from '@mui/joy';

type StateMessageProps = PropsWithChildren<{
  icon: ReactNode;
  /** The tile's colour: primary for the steps of signing in, danger for no access, neutral for nothing to show. */
  color?: ColorPaletteProp;
  /** `solid` for the brand tile of the empty conversation. */
  variant?: 'soft' | 'solid';
  title: ReactNode;
  text?: ReactNode;
}>;

/** A state of the chat in the middle of its area: an icon tile, a title, a line of text and the action under it. */
export const StateMessage = ({
  icon,
  color = 'primary',
  variant = 'soft',
  title,
  text,
  children,
}: StateMessageProps) => (
  <Stack
    alignItems='center'
    justifyContent='center'
    gap={1.5}
    sx={{ flex: 1, minHeight: '100%', px: 3, py: 4, textAlign: 'center' }}
  >
    <Sheet
      variant={variant}
      color={color}
      sx={{
        width: 48,
        height: 48,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 0.5,
        // the mockups' tiles are paler than Joy's soft variant
        ...(variant === 'soft' && { bgcolor: `${color}.50` }),
      }}
    >
      {icon}
    </Sheet>
    <Typography level='title-lg'>{title}</Typography>
    {text && (
      <Typography
        level='body-sm'
        textColor='neutral.600'
        sx={{ maxWidth: 420 }}
      >
        {text}
      </Typography>
    )}
    {children && (
      <Stack alignItems='center' gap={1} sx={{ mt: 1, width: '100%' }}>
        {children}
      </Stack>
    )}
  </Stack>
);

/** «Входимо…»: the session is checked, or the panel waits for the host app's user before showing any chat. */
export const LoadingState = () => (
  <StateMessage
    icon={<CircularProgress size='sm' />}
    title='Входимо…'
    text='Перевіряємо вашу сесію Gasbi. Це займе кілька секунд.'
  />
);
